import { DOCUMENT } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnDestroy, Output, Renderer2 } from '@angular/core';
import { ModalService } from '@app/core/services/modal.service';
import { TextAlign } from '@app/core/services/core-component.service';
import { CustomerService } from '@app/services/customer.service';
import { OrderHeadGet, OrderheadService } from '@app/services/orderhead.service';

@Component({
    selector: 'tt-order-head-convert-to-order-button',
    templateUrl: './order-head-convert-to-order-button.component.html',
    styleUrls: ['./order-head-convert-to-order-button.component.css'],
})
export class OrderHeadConvertToOrderButtonComponent implements OnDestroy {
    @Input()
    public get?: OrderHeadGet;

    @Input()
    public orderHeadProvider?: () => OrderHeadGet | undefined;

    @Output()
    public converted = new EventEmitter<void>();

    @Input()
    public type: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'link' | 'ghost-danger' | 'icon' = 'primary';

    @Input()
    public icon = '';

    @Input()
    public textAlign?: TextAlign;

    @Input()
    public iconAlign: 'left' | 'right' = 'right';

    public converting = false;

    private pageClickOverlay?: HTMLElement;

    private pageKeyboardListeners: (() => void)[] = [];

    private focusedElementBeforeDisable?: HTMLElement | null;

    constructor(
        private orderhead: OrderheadService,
        private customer: CustomerService,
        private modal: ModalService,
        private renderer: Renderer2,
        @Inject(DOCUMENT) private document: Document,
    ) {}

    public async convert() {
        if (this.converting) return;

        const orderHead = this.orderHeadProvider?.() ?? this.get;

        if (!orderHead) return;

        try {
            this.converting = true;

            if (orderHead.setting_confirm_convert_to_order === '1') {
                const confirmed = await this.modal.openConfirmDialog({ type: 'danger', title: 'confirm_title', translateTitle: true, message: 'confirm_convert_message', translateMessage: true });

                if (!confirmed) return;
            }

            this.disablePageClicks();

            const saveResponse = await this.orderhead.saveOrderHead(orderHead);

            if ('' + saveResponse.errorcode !== '0') {
                this.enablePageClicks();
                await this.showWarning(saveResponse.errormessage);
                return;
            }

            const convertResponse = await this.orderhead.convertOrderHeadToOrder(orderHead);

            if ('' + convertResponse.errorcode !== '0') {
                this.enablePageClicks();
                await this.showWarning(convertResponse.errormessage);
                return;
            }

            this.enablePageClicks();
            void this.showCreditStopWarning(orderHead);
            this.converted.emit();
        } catch (error) {
            console.error(error);
        } finally {
            this.enablePageClicks();
            this.converting = false;
        }
    }

    ngOnDestroy(): void {
        this.enablePageClicks();
    }

    private showWarning(message: string) {
        return this.modal.show({ type: 'warning', title: 'warning', translateTitle: true, message });
    }

    private async showCreditStopWarning(orderHead: OrderHeadGet) {
        if (orderHead.show_warning_when_converting_to_order_if_customer_has_creditstop !== '1') return;

        try {
            const customer = await this.customer.getCustomerEdit({ cust_no: orderHead.stat_cust_no });

            if (customer.creditstop === '1') {
                await this.modal.show({ type: 'warning', title: 'warning', translateTitle: true, message: 'msg00774', translateMessage: true });
            }
        } catch (error) {
            console.error(error);
        }
    }

    private disablePageClicks() {
        if (this.pageClickOverlay) return;

        const overlay = this.renderer.createElement('div') as HTMLElement;

        this.focusedElementBeforeDisable = this.document.activeElement instanceof HTMLElement ? this.document.activeElement : undefined;

        this.renderer.setAttribute(overlay, 'tabindex', '-1');
        this.renderer.setStyle(overlay, 'position', 'fixed');
        this.renderer.setStyle(overlay, 'inset', '0');
        this.renderer.setStyle(overlay, 'z-index', '2147483647');
        this.renderer.setStyle(overlay, 'cursor', 'wait');
        this.renderer.setStyle(overlay, 'pointer-events', 'all');
        this.renderer.setStyle(overlay, 'background', 'transparent');
        this.renderer.setStyle(overlay, 'outline', 'none');

        this.renderer.appendChild(this.document.body, overlay);
        this.pageClickOverlay = overlay;
        overlay.focus({ preventScroll: true });

        this.disablePageKeyboard();
    }

    private enablePageClicks() {
        if (!this.pageClickOverlay) return;

        this.renderer.removeChild(this.document.body, this.pageClickOverlay);
        this.pageClickOverlay = undefined;
        this.enablePageKeyboard();
        this.focusedElementBeforeDisable?.focus({ preventScroll: true });
        this.focusedElementBeforeDisable = undefined;
    }

    private disablePageKeyboard() {
        const blockKeyboard = (event: KeyboardEvent) => {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        };

        this.document.addEventListener('keydown', blockKeyboard, true);
        this.document.addEventListener('keypress', blockKeyboard, true);
        this.document.addEventListener('keyup', blockKeyboard, true);

        this.pageKeyboardListeners = [() => this.document.removeEventListener('keydown', blockKeyboard, true), () => this.document.removeEventListener('keypress', blockKeyboard, true), () => this.document.removeEventListener('keyup', blockKeyboard, true)];
    }

    private enablePageKeyboard() {
        this.pageKeyboardListeners.forEach((removeListener) => removeListener());
        this.pageKeyboardListeners = [];
    }
}
